// Dancers stored twice – once with Greek letters, once with Latin letters – that are really one person.
//
//   same   – identical name once script / case / word order are ignored (e.g. "Zoi Pratti" = "Ζωή Πράττη")
//   likely – a Greek-letter name that is very close to a Latin-letter name but not identical
//            (spelling variants such as Papadopoulos / Papadopoulou, a different transliteration …)

import { nameKey, nameTokens, nameSimilarity } from './dancerMatching';

const GREEK = /[Ͱ-Ͽἀ-῿]/;
export const hasGreek = (s) => GREEK.test(s || '');

// names whose Greek letters were lost at upload ("?????") cannot be matched
const usable = (name) => !!name && !/\?{2,}/.test(name) && /\p{L}/u.test(name);

const LIKELY_THRESHOLD = 0.86;
const MAX_LIKELY_CANDIDATES = 3;

/**
 * @param dancers  [{ name, totalResults, studios, competitions, ... }]
 * @returns [{ id, kind: 'same'|'likely', score, dancers: [...] }]  – 'same' groups first
 */
export function findScriptDuplicates(dancers) {
  const clean = (dancers || []).filter((d) => usable(d.name));
  const groups = [];
  const used = new Set();

  // 1) same name in both scripts
  const byKey = new Map();
  clean.forEach((d) => {
    const key = nameKey(d.name);
    if (!key) return;
    if (!byKey.has(key)) byKey.set(key, []);
    byKey.get(key).push(d);
  });
  byKey.forEach((list, key) => {
    if (list.length < 2) return;
    if (!list.some((d) => hasGreek(d.name)) || !list.some((d) => !hasGreek(d.name))) return;
    groups.push({
      id: `same:${key}`,
      kind: 'same',
      score: 100,
      dancers: [...list].sort((a, b) => b.totalResults - a.totalResults)
    });
    list.forEach((d) => used.add(d.name));
  });

  // 2) Greek-letter names that are close to (but not the same as) a Latin-letter name
  const latin = clean
    .filter((d) => !hasGreek(d.name) && !used.has(d.name))
    .map((d) => {
      const tokens = nameTokens(d.name);
      return { d, tokens, prefixes: new Set(tokens.map((t) => t.slice(0, 2))) };
    });

  clean.filter((d) => hasGreek(d.name) && !used.has(d.name)).forEach((greek) => {
    const tokens = nameTokens(greek.name);
    if (tokens.length < 2) return;
    const wanted = new Set(tokens.map((t) => t.slice(0, 2)));
    const found = [];
    latin.forEach(({ d, tokens: t, prefixes }) => {
      let share = false;
      prefixes.forEach((p) => { if (wanted.has(p)) share = true; });
      if (!share) return;
      const score = nameSimilarity(tokens, t);
      if (score >= LIKELY_THRESHOLD) found.push({ d, score });
    });
    if (!found.length) return;
    found.sort((a, b) => b.score - a.score);
    const top = found.slice(0, MAX_LIKELY_CANDIDATES);
    groups.push({
      id: `likely:${greek.name}`,
      kind: 'likely',
      score: Math.round(top[0].score * 100),
      dancers: [greek, ...top.map((c) => ({ ...c.d, similarity: Math.round(c.score * 100) }))]
    });
  });

  return groups.sort((a, b) => (a.kind === b.kind ? b.score - a.score || b.dancers.length - a.dancers.length : a.kind === 'same' ? -1 : 1));
}

/**
 * Which dancer should survive a merge. The merge tool deletes the other profiles' documents, so a
 * claimed profile / uploaded photo wins; then the Latin-letter spelling; then the one with most results.
 */
export function pickPrimary(dancers, profileInfo = {}) {
  const info = (d) => profileInfo[d.name] || {};
  return [...dancers].sort((a, b) =>
    (info(b).claimed ? 1 : 0) - (info(a).claimed ? 1 : 0) ||
    (info(b).photo ? 1 : 0) - (info(a).photo ? 1 : 0) ||
    (hasGreek(a.name) ? 1 : 0) - (hasGreek(b.name) ? 1 : 0) ||
    b.totalResults - a.totalResults
  );
}
